'use client';

import {
  useIsWalletMatch,
  WalletInput,
} from '#/components/ethereum/wallet-input';
import { Button } from '#/components/ui/button';
import {
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '#/components/ui/dialog';
import { Label } from '#/components/ui/label';
import { EthereumStakingValidatorInfo } from '#/data/react-query/queries/order-list';
import { useIsCurrentEpochPastTarget } from '#/data/v2/useEthereumCurrentEpoch';
import { useWriteDirectStakingExit } from '#/generated';
import { cn } from '#/lib/utils';
import {
  TransactionDetails,
  TransactionViewOnButton,
} from '#/modules/display/transaction';
import { ValidatorRow } from '#/modules/display/validator-row';
import { WithdrawalQueueTip } from '#/modules/queue-withdrawal';
import { DialogContentProps } from '@radix-ui/react-dialog';
import { Translation } from 'intl-lib';
import { Loader2 } from 'lucide-react';

interface Props {
  originalRow: EthereumStakingValidatorInfo;
}
function DialogContentImpl({ originalRow }: Props) {
  const isWalletMatch = useIsWalletMatch(originalRow.claimaddr);

  const isExitable = useIsCurrentEpochPastTarget(originalRow.activeEpoch, 2048);

  const {
    writeContractAsync: exitAsync,
    data: transactionHash,
    isPending,
    isSuccess,
  } = useWriteDirectStakingExit();

  return (
    <>
      <DialogHeader>
        <DialogTitle>
          <Translation path="my_staking.exit_dialog.title" />
        </DialogTitle>
      </DialogHeader>
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <Label>
            <Translation path="EthereumStaking.ClaimAddress" />
          </Label>
          <div>
            <WalletInput address={originalRow.claimaddr} />
          </div>
        </div>
        <div className="flex flex-col gap-2">
          <Label>
            <Translation path="my_staking.exit_dialog.validators" />
          </Label>
          <div>
            <ValidatorRow item={originalRow} />
          </div>
        </div>
        {isSuccess && (
          <div className="flex flex-col gap-2">
            <Label>Transaction</Label>
            <div>
              <TransactionDetails transactionHash={transactionHash} />
            </div>
          </div>
        )}
        <WithdrawalQueueTip />
      </div>
      <DialogFooter>
        <DialogClose asChild>
          <Button type="button" variant="secondary">
            <Translation path="Application.Dialog.CancelButtonLabel" />
          </Button>
        </DialogClose>
        {isSuccess ? (
          <TransactionViewOnButton transactionHash={transactionHash} />
        ) : (
          <Button
            type="button"
            variant="primary"
            disabled={!isExitable || !isWalletMatch || isPending}
            onClick={() =>
              exitAsync({
                args: [BigInt(originalRow.validatorId)],
              })
            }
          >
            {isPending && <Loader2 className="animate-spin" />}
            <Translation path="EthereumStaking.ExitAction.ButtonLabel" />
          </Button>
        )}
      </DialogFooter>
    </>
  );
}

export function ExitDialogContent({
  originalRow,
  className,
  ...props
}: Props & DialogContentProps) {
  return (
    <DialogContent
      {...props}
      className={cn(
        'sm:max-w-(--breakpoint-sm) overflow-auto max-h-screen',
        className
      )}
    >
      <DialogContentImpl originalRow={originalRow} />
    </DialogContent>
  );
}
